/** @format */

import {
	AnalyticsService,
	BreakpointsService,
	FileService,
	UtilityService
} from '@shared/data-access/services';

import {
	ChangeDetectionStrategy,
	Component,
	ComponentRef,
	computed,
	effect,
	inject,
	input,
	signal,
	TemplateRef,
	viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import { ProcessedNonBundleProduct, Shipping } from '@shared/data-access/interfaces';
import { filter, take } from 'rxjs/operators';
import { selectPrimaryCategory, selectShippingData } from '@pdp/data-access/state';

import { DEFAULT_SHIPPING_MESSAGES } from './default-shipping-messages.constants';

import {
	homefieldIconAlertErrorOutlined,
	HomefieldIconLibraryModule,
	homefieldIconOffer,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { BypassSanitizationPipe } from '@shared/utils/pipes';
import { Store as NgRxStore } from '@ngrx/store';
import { pdpJsonFileName } from '@pdp/data-access/constants';
import { toSignal } from '@angular/core/rxjs-interop';

export interface IShippingMessage {
	analyticsModalName: string;
	date?: number;
	emsName: string;
	image: string;
	imageClass: string;
	innerHtml: string;
	message: string;
	order: number;
	show: boolean;
	showImage: boolean;
	subMessage: string;
	type: string;
}

export interface IShippingAttributes {
	date?: number;
	innerHtml?: string;
	message?: string;
	show?: boolean;
	subMessage?: string;
}

@Component({
	selector: 'pdp-component-shipping',
	templateUrl: './shipping.component.html',
	styleUrl: './shipping.component.scss',
	imports: [DatePipe, HomefieldIconLibraryModule, BypassSanitizationPipe],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShippingComponent {
	private readonly fileService = inject(FileService);
	private readonly ngRxstore = inject(NgRxStore);
	private readonly productSelectionUtilityService = inject(ProductSelectionUtilityService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly homrDataService = inject(HomrDataService);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly breakpointsService = inject(BreakpointsService);

	productStyle = input<ProcessedNonBundleProduct>();
	/**
	 * @description
	 * Enable pre order text and date change
	 */
	preOrderEnabled = input<boolean>();

	// Reactive signals from store and data sources
	private readonly shippingDataFromStore = toSignal(
		this.ngRxstore
			.select(selectShippingData)
			.pipe(filter((data): data is Shipping => data != null && 'shippingClass' in data))
	);

	private readonly primaryCategoryFromStore = toSignal(
		this.ngRxstore.select(selectPrimaryCategory)
	);

	private readonly brandsData = toSignal(
		this.fileService.getDataFileByType$(pdpJsonFileName.brandsData, 'json'),
		{ initialValue: undefined }
	);

	private readonly categoriesData = toSignal(
		this.fileService.getDataFileByType$(pdpJsonFileName.categoriesData, 'json'),
		{ initialValue: undefined }
	);

	/**
	 * @description
	 * Flag to determine if the product is in the "bike" category based on the calc code (10 and 20 should not display oversized shipping messaging)
	 */
	protected readonly isBikeCategory = computed(() => {
		const shippingData = this.shippingDataFromStore();
		const primaryCategoryData = this.primaryCategoryFromStore();
		const categoriesJson = this.categoriesData();

		// Return false if not applicable
		if (!shippingData || shippingData.isHotMarket || !primaryCategoryData || !categoriesJson) {
			return false;
		}

		// Check the note field as primary name, and fallback to standard category name
		const categoryName =
			typeof primaryCategoryData.note === 'string' && primaryCategoryData.note.trim() !== ''
				? primaryCategoryData.note
				: primaryCategoryData.name;

		// Check if category is in exclusion list
		return categoriesJson?.oversizeShippingExclusion?.indexOf(categoryName) >= 0;
	});

	/**
	 * @description
	 * Shipping messages to be displayed
	 */
	protected readonly shippingMessages = computed(() => {
		// Trigger recomputation when these signals change
		const shippingData = this.shippingInfo();
		const dynamicContent = this.dynamicShippingContent();

		let messages = this.getShippingMessagesToDisplay(shippingData);

		// Apply any dynamically loaded content
		if (dynamicContent) {
			messages = this.setShippingAttributesForType(messages, dynamicContent.type, {
				innerHtml: dynamicContent.html
			});
		}

		return messages;
	});

	/**
	 * @description
	 * Dynamically loaded shipping content from HOMR service
	 */
	private readonly dynamicShippingContent = signal<{ type: string; html: string } | null>(null);

	/**
	 * @description
	 * Computed signal to determine if shipping messages should be displayed
	 * Derives from shippingMessages by checking if any message has show: true
	 */
	protected readonly shouldDisplayShippingMessages = computed(() => {
		const messages = this.shippingMessages();
		return messages.some((msg) => msg.show);
	});

	/**
	 * Flag to determine if ship lead message should be displayed
	 */
	protected readonly shouldHideShipLeadMessage = computed(() => {
		const shippingData = this.shippingDataFromStore();
		const brandJsonData = this.brandsData();

		if (shippingData && brandJsonData?.hideLeadTimeBrands) {
			return this.shouldBrandHideShipLeadMessage(brandJsonData.hideLeadTimeBrands);
		}
		return false;
	});

	/**
	 * @description
	 * Shipping info observable.
	 */
	protected readonly shippingInfo = computed(() => this.shippingDataFromStore());

	/**
	 * @description
	 * Current shipping message that the user has selected.
	 */
	protected readonly currentShippingMessage = signal<IShippingMessage | null>(null);

	private readonly drawerRef = signal<ComponentRef<HmfDrawerComponent> | null>(null);
	drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');

	constructor(homefieldIconsLibraryService: HomefieldIconsLibraryService) {
		homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertErrorOutlined,
			homefieldIconOffer
		]);

		// Effect to update drawer size on breakpoint changes
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			const drawer = this.drawerRef();
			if (drawer) {
				drawer.setInput(
					'size',
					this.productSelectionUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						breakpoint
					)
				);
			}
		});
	}

	/**
	 * @description
	 * Accepts the lead time number and builds the appropriate message
	 *
	 * @param leadTime
	 */
	getLeadTimeMessage(leadTime: number): string {
		let leadMessage;

		if (leadTime > 2 && leadTime < 11) {
			leadMessage = `Usually ships in ${leadTime} business days.`;
		} else if (leadTime % 5 === 0 && leadTime <= 55) {
			leadMessage = `Usually ships in ${leadTime / 5} weeks.`;
		} else if (leadTime % 5 !== 0 && leadTime <= 55) {
			// Calculate number of weeks (Using bitwise operator to round)
			leadMessage = `Usually ships in ${(leadTime / 5) >> 0} - ${
				(leadTime / 5 + 1) >> 0
			} weeks.`;
		} else {
			leadMessage = 'Usually ships in 11 or more weeks.';
		}

		return leadMessage;
	}

	private determineDisplayableShipMessages(shippingInfo: Shipping | undefined) {
		const isLTL = shippingInfo?.shippingClass?.indexOf('LTL') > -1;
		const isOP = shippingInfo?.shippingClass?.indexOf('OP') > -1;
		const isP = shippingInfo?.shippingClass?.indexOf('P') > -1;

		return {
			LTL: isLTL,
			OP: isOP,
			standard: isP || isOP,
			restriction: shippingInfo?.isHazmat || shippingInfo?.isRestricted,
			leadTime:
				shippingInfo?.leadTime > 2 &&
				!shippingInfo?.isHotMarket &&
				!this.shouldHideShipLeadMessage() &&
				!shippingInfo?.isPreSale,
			preOrder:
				shippingInfo?.isPreSale &&
				!shippingInfo?.comingSoon &&
				UtilityService.isDateAfterNowDate(shippingInfo.preSaleDate, 'MM-dd-yyyy')
		};
	}

	/**
	 * @description
	 * Uses a Shipping object to determine the shipping messages to be displayed
	 * Note: This method is now PURE - no side effects (no signal.set() calls)
	 *
	 * @param shippingInfo
	 */
	getShippingMessagesToDisplay(shippingInfo: Shipping | undefined): IShippingMessage[] {
		// Early return if no shipping info
		if (!shippingInfo) {
			return JSON.parse(JSON.stringify(DEFAULT_SHIPPING_MESSAGES));
		}

		let displayedShippingMessages: IShippingMessage[] = JSON.parse(
			JSON.stringify(DEFAULT_SHIPPING_MESSAGES)
		);

		const displayableMessages = this.determineDisplayableShipMessages(shippingInfo);

		if (displayableMessages.LTL) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'LTL',
				{ show: true }
			);
		}

		if (displayableMessages.OP) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'OP',
				{ show: !this.isBikeCategory() }
			);
		}

		if (displayableMessages.standard) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'Standard',
				{ show: false }
			);
		}

		if (displayableMessages.restriction) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'Restriction',
				{ show: true }
			);
		}

		if (displayableMessages.leadTime) {
			displayedShippingMessages = this.setShippingAttributesForType(
				displayedShippingMessages,
				'LeadTime',
				{ show: true, message: this.getLeadTimeMessage(shippingInfo.leadTime) }
			);
		}

		if (displayableMessages.preOrder) {
			if (this.preOrderEnabled()) {
				displayedShippingMessages = this.setShippingAttributesForType(
					displayedShippingMessages,
					'PreOrder',
					{
						show: true,
						subMessage: 'Ships after ',
						date: this.productSelectionUtilityService.formatDateToTimestamp(
							shippingInfo.preSaleDate
						)
					}
				);
			} else {
				displayedShippingMessages = this.setShippingAttributesForType(
					displayedShippingMessages,
					'PreOrder',
					{
						show: true,
						subMessage: `Available to start shipping ${shippingInfo.preSaleDate}`
					}
				);
			}
		}

		// Sort by display value
		displayedShippingMessages.sort((a: IShippingMessage, b: IShippingMessage) =>
			a.order > b.order ? 1 : -1
		);

		return displayedShippingMessages;
	}

	openDrawer() {
		const headerText = 'Shipping Details';
		const drawer = this.drawerRef();
		if (!drawer) {
			this.drawerRef.set(
				this.modalFactoryService.create(
					HmfDrawerComponent,
					{
						headerText,
						closable: true,
						mobilePeek: true,
						direction: 'right',
						isCloseButtonSticky: true,
						size: this.productSelectionUtilityService.getValueByScreenSize(
							DynamicDrawerSizes,
							this.breakpointsService.breakpoint()
						)
					},
					this.drawerContent()
				)
			);
		}

		const updatedDrawer = this.drawerRef();
		if (updatedDrawer) {
			updatedDrawer.instance.openDrawer();
		}
		this.sendModalOpenAnalytics(
			this.currentShippingMessage()?.analyticsModalName ?? headerText
		);
	}

	/**
	 * @description
	 * Loads the shipping message details JSON
	 *
	 * @param shippingMessage
	 */
	loadBodyContent(shippingMessage: IShippingMessage) {
		// If content needs to be loaded dynamically, wait for it before opening drawer
		if (typeof shippingMessage !== 'undefined' && shippingMessage.innerHtml === '') {
			this.homrDataService
				.getData$('static', 2, shippingMessage.emsName)
				.pipe(take(1))
				.subscribe((homrTemplate: HomrPage) => {
					const content =
						homrTemplate.content['Informationals Content'] ||
						homrTemplate.content['informationalsContent'] ||
						homrTemplate.content['Informational Details Alpha'];
					if (content?.[0]) {
						const htmlContent = decodeURIComponent(content[0].wysiwyg);

						// Update dynamicShippingContent for reactive updates
						this.dynamicShippingContent.set({
							type: shippingMessage.type,
							html: htmlContent
						});

						// Create updated message with loaded content
						const updatedMessage: IShippingMessage = {
							...shippingMessage,
							innerHtml: htmlContent
						};

						// Set current message with loaded content
						this.currentShippingMessage.set(updatedMessage);
					} else {
						// No content found, set message as-is
						this.currentShippingMessage.set(shippingMessage);
					}

					// Open drawer AFTER content is loaded and message is updated
					this.openDrawer();
				});
		} else {
			// Content already exists, set message and open drawer immediately
			this.currentShippingMessage.set(shippingMessage);
			this.openDrawer();
		}
	}

	/**
	 * @description
	 * Accepts an array of IShippingMessage objects and iterates them looking for the provided type and assigns any value overrides
	 * that are provided
	 *
	 * @param shippingMessages
	 * @param shippingType
	 * @param shippingAttributes
	 */
	setShippingAttributesForType(
		shippingMessages: IShippingMessage[],
		shippingType: string,
		shippingAttributes: IShippingAttributes = { show: false }
	): IShippingMessage[] {
		// Ensure there is something to iterate
		if (Array.isArray(shippingMessages) && shippingMessages.length > 0) {
			// Iterate the shipping messages
			for (let i = 0, length = shippingMessages.length; i < length; i += 1) {
				// Ensure there is a defined object at the index and if the current shipping message matches the argument
				if (
					typeof shippingMessages[i] !== 'undefined' &&
					shippingMessages[i].type === shippingType
				) {
					// Assign any attributes
					shippingMessages[i] = Object.assign(shippingMessages[i], shippingAttributes);
					// Break out to prevent unnecessary looping
					break;
				}
			}
		}
		// Return the shipping messages
		return shippingMessages;
	}

	public shouldBrandHideShipLeadMessage(brands: string[]): boolean {
		const brandAttributeName = 'X_BRAND';
		return !!this.productStyle()?.descriptiveAttributes.find((attrs) => {
			if (attrs.identifier === brandAttributeName) {
				return brands.includes(attrs.value.toString());
			}
		});
	}

	/**
	 * @description Calls analytics service to trigger an event for the modal being opened.
	 */
	private sendModalOpenAnalytics(name: string) {
		/*
		 * Analytics Event
		 */
		// Set the modal name
		this.analyticsService.setModalName(name);

		// Set the Analytics event type
		this.analyticsService.setType('event', 'ModalOpen');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('reporting:update');
	}

	trackByFn(index: number, item: IShippingMessage): string {
		return index.toString() + item.emsName;
	}
}
